import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import { CompetitionService } from '../../service/CompetitionService';
import useNavProps from '../../hooks/useNavProps';
import CompetitionToolbar from './CompetitionToolbar';
import { DataTable } from 'primereact/datatable';
import DataTableUtils from '../../utilities/DataTableUtils';
import { Column } from 'primereact/column';
import { Toolbar } from 'primereact/toolbar';
import SecurityService from '../../service/SecurityService';
import { Button } from 'primereact/button';
import JobEditDialog from './JobEditDialog';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import DeleteModalComponent from '../../components/DeleteModalComponent';
import CompetitionUtils from '../../utilities/CompetitionUtils';
import JobViewDialog from './JobViewDialog';
import GeneralUtils from '../../utilities/GeneralUtils';

const CompetitionJob = () => {
    const [competition, setCompetition] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [jobs, setJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [hasEditPermission, setHasEditPermission] = useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [viewDialogVisible, setViewDialogVisible] = useState(false);

    const toast = useRef(null);
    const dt = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    const { competitionIdParam } = useParams();
    const { setNavPropsValue } = useNavProps();

    useEffect(() => {
        setIsLoading(true);
        const checkEditPermission = async () => {
            let _hasEditPermission = await SecurityService.check('CAN_EDIT_JOB_COMPETITION', competitionIdParam);
            setHasEditPermission(_hasEditPermission);
            return _hasEditPermission;
        };

        const getInstitutions = async () => {
            await competitionService.getInstitutionsForCompetitionByCriteria(competitionIdParam, { name: null })
                .then((_institutions) => {
                    setInstitutions(_institutions);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        const getCompetition = async () => {
            await competitionService.getCompetition(competitionIdParam)
                .then(async (competitionData) => {
                    // console.log(competitionData);
                    setCompetition(competitionData);

                    let _hasEditPermission = await checkEditPermission()
                    await getJobs(competitionIdParam)
                    if (_hasEditPermission) {
                        await getInstitutions();
                    }

                    setNavPropsValue('competition-job', competitionData.id, competitionData.name)

                    setIsLoading(false);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getCompetition();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionService, competitionIdParam]);

    const getJobs = async (competitionId) => {
        await competitionService.getCompetitionJobs(competitionId)
            .then((_jobs) => {
                // console.log(_jobs)
                setJobs(_jobs);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const showNewDialog = () => {
        setSelectedJob(null);
        setEditDialogVisible(true);
    };

    const showEditDialog = (_job) => {
        setSelectedJob(_job);
        setEditDialogVisible(true);
    };

    const showViewDialog = (_job) => {
        setSelectedJob(_job);
        setViewDialogVisible(true);
    };

    const showDeleteDialog = (_job) => {
        setSelectedJob(_job);
        setDeleteDialogVisible(true);
    };

    const afterSave = (savedCompetition, success, error) => {
        if (success) {
            // console.log("savedCompetition: ", savedCompetition);
            getJobs(competition?.id)
            toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
        } else {
            if (error?.hasValidationError) {
                setValidationErrors(error.errorCodes)
                setShowValidationErrorDialog(true)
            } else {
                error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
            }
        }
    };

    const deleteJob = () => {
        competitionService.deleteJob(selectedJob.id)
            .then(() => {
                getJobs(competition?.id)
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteDialogVisible(false);
                setSelectedJob(null);
            });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    {hasEditPermission &&
                        <>
                            <Button label={translatedMessage('job.add')} icon="pi pi-plus" className="p-button-primary mr-2" onClick={showNewDialog}
                                disabled={!CompetitionUtils.canEdit(competition)} />
                        </>
                    }
                </div>
            </React.Fragment>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-primary m-1"
                    onClick={() => showViewDialog(rowData)}
                    tooltip={translatedMessage('generic.edit')}
                    tooltipOptions={{ position: 'top' }}
                />
                {hasEditPermission && (
                    <>
                        <Button
                            icon="pi pi-pencil"
                            className="p-button-rounded p-button-info m-1"
                            onClick={() => showEditDialog(rowData)}
                            tooltip={translatedMessage('generic.edit')}
                            tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                            disabled={!CompetitionUtils.canEdit(competition)}
                        />
                        <Button
                            icon="pi pi-trash"
                            className="p-button-rounded p-button-danger m-1"
                            onClick={() => showDeleteDialog(rowData)}
                            tooltip={translatedMessage('generic.delete')}
                            tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                            disabled={!CompetitionUtils.canEdit(competition)}
                        />
                    </>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <CompetitionToolbar competition={competition} code="JOB" />

                            <h4 className="mb-0">{translatedMessage('competition.jobs')}</h4>

                            <Toolbar left={leftToolbarTemplate} className="px-0 pt-0"></Toolbar>

                            <DataTable
                                ref={dt}
                                value={jobs}
                                dataKey="id"
                                paginator
                                rows={DataTableUtils.defalRowsPerPage()}
                                rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                                className="datatable-responsive pcn-datatable"
                                responsiveLayout="scroll"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                                emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                sortField="jobID"
                                sortOrder={1}
                            >
                                <Column
                                    field="institution.name"
                                    header={translatedMessage('institution.institution')}
                                    sortable
                                    headerStyle={{ width: '15%', minWidth: '10rem' }}
                                />
                                <Column
                                    field="departmentType"
                                    header={translatedMessage('job.departmentType')}
                                    sortable
                                    headerStyle={{ width: '15%', minWidth: '10rem' }}
                                />
                                <Column
                                    field="departmentName"
                                    header={translatedMessage('job.departmentName')}
                                    sortable
                                    headerStyle={{ width: '15%', minWidth: '10rem' }}
                                />
                                <Column
                                    field="position"
                                    header={translatedMessage('job.position')}
                                    sortable
                                    headerStyle={{ width: '15%', minWidth: '10rem' }}
                                />
                                <Column
                                    field="jobClass"
                                    header={translatedMessage('job.jobClass')}
                                    sortable
                                    headerStyle={{ width: '10%', minWidth: '10rem' }}
                                />
                                <Column
                                    field="level"
                                    header={translatedMessage('job.level')}
                                    sortable
                                    headerStyle={{ width: '10%', minWidth: '10rem' }}
                                />
                                <Column
                                    field="jobID"
                                    header={translatedMessage('job.jobID')}
                                    sortable
                                    headerStyle={{ width: '10%', minWidth: '10rem' }}
                                    body={(e) => GeneralUtils.dashIfNullForObject(e.jobID)}
                                />
                                <Column body={actionBodyTemplate} />
                            </DataTable>
                        </div>
                    </div>
                </div>

                {editDialogVisible &&
                    <JobEditDialog
                        value={selectedJob}
                        competition={competition}
                        visible={editDialogVisible}
                        afterSave={afterSave}
                        visibleSetter={() => setEditDialogVisible(false)}
                        existingJobs={jobs}
                        institutions={institutions}
                    />
                }

                {viewDialogVisible &&
                    <JobViewDialog
                        value={selectedJob}
                        visible={viewDialogVisible}
                        visibleSetter={() => setViewDialogVisible(false)}
                    />
                }

                {showValidationErrorDialog &&
                    <ValidationErrorModalComponent
                        visible={showValidationErrorDialog}
                        errors={validationErrors}
                        closeDialog={() => setShowValidationErrorDialog(false)}
                    />
                }

                {deleteDialogVisible &&
                    <DeleteModalComponent
                        visible={deleteDialogVisible}
                        item={selectedJob?.position + " - " + selectedJob?.jobID}
                        closeDialog={() => {
                            setDeleteDialogVisible(false);
                        }}
                        deleteRecord={() => deleteJob()}
                    />
                }
            </>
        );
    }
};

export default CompetitionJob;
